/**
 * F-201: the visitor's cookie choice for a community's own Google Analytics.
 *
 * Only what is strictly necessary runs without a choice. The community's GA
 * loads after "Aceitar" (or "Medição" on in "Personalizar"); refusing keeps it
 * off on this visit and the next ones, and removes the `_ga` cookies. The
 * choice lives in this browser only (no proof on the server, decided in the
 * item), per community, for 12 months.
 */

export type CookieChoice = 'granted' | 'denied'

interface StoredChoice {
  analytics: boolean
  decidedAt: string
}

const KEY_PREFIX = 'memberfy_cookie_consent:'
const VALID_MS = 365 * 24 * 60 * 60 * 1000

/** Fired with `{ communityId, choice }` whenever the choice changes on this page. */
export const COOKIE_CHOICE_EVENT = 'memberfy:cookie-choice'
/** Fired by "Cookies" (side menu and profile panel): opens the notice again. */
export const OPEN_COOKIE_NOTICE_EVENT = 'memberfy:open-cookie-notice'

/** The stored choice for this community, or null when none was made or it is older than 12 months. */
export function readCookieChoice(communityId: string | null | undefined): CookieChoice | null {
  if (!communityId) return null
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + communityId)
    if (!raw) return null
    const stored = JSON.parse(raw) as StoredChoice
    const decidedAt = new Date(stored.decidedAt).getTime()
    if (!Number.isFinite(decidedAt) || Date.now() - decidedAt > VALID_MS) {
      window.localStorage.removeItem(KEY_PREFIX + communityId)
      return null
    }
    return stored.analytics ? 'granted' : 'denied'
  } catch {
    return null
  }
}

export function saveCookieChoice(communityId: string, choice: CookieChoice) {
  try {
    const stored: StoredChoice = { analytics: choice === 'granted', decidedAt: new Date().toISOString() }
    window.localStorage.setItem(KEY_PREFIX + communityId, JSON.stringify(stored))
  } catch {
    /* Storage blocked: the choice still holds for this page. */
  }
  window.dispatchEvent(new CustomEvent(COOKIE_CHOICE_EVENT, { detail: { communityId, choice } }))
}

export function openCookieNotice() {
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_NOTICE_EVENT))
}

/** The host and each parent domain a cookie may have been set on (`a.b.memberfy.dev` → itself, `b.memberfy.dev`, `memberfy.dev`). */
export function cookieDomainsOf(hostname: string): string[] {
  const parts = hostname.split('.')
  const domains: string[] = []
  for (let i = 0; i < parts.length - 1; i++) domains.push(parts.slice(i).join('.'))
  return domains
}

/**
 * Removes GA's cookies (`_ga`, `_ga_<id>`, `_gid`, `_gat*`) from the host and
 * every parent domain: an older GA set them on the parent domain, and the
 * browser only drops a cookie when the domain matches.
 */
export function deleteGaCookies() {
  const names = document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((name) => /^_ga($|_)|^_gid$|^_gat/.test(name))
  if (names.length === 0) return
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'
  for (const name of new Set(names)) {
    document.cookie = `${name}=; ${expired}`
    for (const domain of cookieDomainsOf(window.location.hostname)) {
      document.cookie = `${name}=; ${expired}; domain=${domain}`
      document.cookie = `${name}=; ${expired}; domain=.${domain}`
    }
  }
}
